import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriFeedbackModal.module.scss";

// Coach's serializer caps feedback_text here, so the field must too.
const MAX_LENGTH = 2000;

interface OriFeedbackModalProps {
  /** Non-empty when the learner already left a comment, which switches to edit mode. */
  existingText: string;
  isSubmitting: boolean;
  onSubmit: (text: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onDismiss: () => void;
}

const OriFeedbackModal: React.FC<OriFeedbackModalProps> = ({
  existingText,
  isSubmitting,
  onSubmit,
  onDelete,
  onDismiss,
}) => {
  const { t } = useTranslation(["ori", "common"]);
  const [text, setText] = useState(existingText);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const isEditMode = Boolean(existingText);
  // Emptying an existing comment is a deletion, not an update.
  const isDeleteMode = isEditMode && !text.trim();

  const [idleKey, pendingKey] = isDeleteMode
    ? (["deleteButton", "deletingButton"] as const)
    : isEditMode
      ? (["updateButton", "updatingButton"] as const)
      : (["submitButton", "submittingButton"] as const);
  const submitLabel = t(
    `ori:chat.feedback.modal.${isSubmitting ? pendingKey : idleKey}`
  );

  const handleSubmit = () => {
    if (isDeleteMode) {
      setIsConfirmingDelete(true);
      return;
    }
    return onSubmit(text);
  };

  return (
    <Modal
      isOpen
      centered
      toggle={onDismiss}
      className={styles.modal}
      contentClassName={styles.content}
    >
      <ModalBody className={styles.body} data-testid="ori-feedback-modal">
        <h2 className={styles.title}>
          {isEditMode
            ? t("ori:chat.feedback.modal.editTitle")
            : t("ori:chat.feedback.modal.title")}
        </h2>
        <label className={styles.label} htmlFor="ori-feedback-text">
          {t("ori:chat.feedback.modal.label")}
        </label>
        <textarea
          id="ori-feedback-text"
          className={styles.textarea}
          value={text}
          maxLength={MAX_LENGTH}
          rows={5}
          placeholder={t("ori:chat.feedback.modal.placeholder")}
          onChange={(event) => {
            setText(event.target.value);
            setIsConfirmingDelete(false);
          }}
          data-testid="ori-feedback-textarea"
        />
        {isConfirmingDelete && (
          <div className={styles.confirm} data-testid="ori-feedback-confirm">
            <p className={styles.confirmTitle}>
              {t("ori:chat.feedback.modal.deleteConfirmTitle")}
            </p>
            <p className={styles.confirmBody}>
              {t("ori:chat.feedback.modal.deleteConfirmBody")}
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <Button
          variant="secondary"
          onClick={onDismiss}
          data-testid="ori-feedback-cancel"
        >
          {t("common:action.cancel")}
        </Button>
        <Button
          variant="enclosed-primary"
          monochromeColour="white"
          loading={isSubmitting}
          disabled={!isEditMode && !text.trim()}
          onClick={isConfirmingDelete ? onDelete : handleSubmit}
          data-testid="ori-feedback-submit"
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OriFeedbackModal;
